import { Head, Link, router, usePage } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import FormSelect from '@/Components/FormSelect';
import Alert from '@/Components/Alert';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import { PlusIcon, PencilSquareIcon, TrashIcon, ClipboardDocumentListIcon, MagnifyingGlassIcon, FunnelIcon, CheckIcon, XMarkIcon, DocumentTextIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

export default function Index({ requisitions, filters, flash }) {
    const { auth } = usePage().props;
    const userPerms = auth?.user?.permissions || [];
    const isAdmin = auth?.user?.is_admin;
    const can = (perm) => isAdmin || userPerms.includes(perm);

    const [search, setSearch] = useState(filters?.search || '');
    const [status, setStatus] = useState(filters?.status || '');
    const [materialStatus, setMaterialStatus] = useState(filters?.material_status || '');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [rejectModal, setRejectModal] = useState(null);
    const [rejectedReason, setRejectedReason] = useState('');

    const applyFilters = (e) => {
        e?.preventDefault();
        router.get('/materials', { search, status, material_status: materialStatus }, { preserveState: true });
    };
    const clearFilters = () => {
        setSearch('');
        setStatus('');
        setMaterialStatus('');
        router.get('/materials');
    };

    const statusBadge = (s) => {
        const map = { pending: 'warning', approved: 'success', rejected: 'danger' };
        return <Badge variant={map[s] || 'neutral'}>{s || 'Unknown'}</Badge>;
    };

    const materialStatusBadge = (s) => {
        const map = { open: 'info', closed: 'neutral' };
        return <Badge variant={map[s] || 'neutral'}>{s || 'open'}</Badge>;
    };

    const handleApprove = (id) => {
        router.post(`/materials/${id}/approve`, {}, { preserveScroll: true });
    };

    const handleReject = () => {
        if (!rejectModal) return;
        router.post(`/materials/${rejectModal}/reject`, { rejected_reason: rejectedReason }, { preserveScroll: true });
        setRejectModal(null);
        setRejectedReason('');
    };

    const handleToggleStatus = (id) => {
        router.post(`/materials/${id}/toggle-status`, {}, { preserveScroll: true });
    };

    return (
        <AppLayout>
            <Head title="Materials Requisition" />
            <PageHeader
                title="Materials Requisition"
                subtitle="Manage material requisitions"
                actions={can('materials.create') ? (
                    <Button href="/materials/create" size="sm">
                        <PlusIcon className="w-4 h-4" /> New Requisition
                    </Button>
                ) : null}
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={applyFilters} className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search requisitions..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20"
                            />
                        </div>
                        <FormSelect value={status} onChange={e => setStatus(e.target.value)} className="w-full sm:w-40">
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </FormSelect>
                        <FormSelect value={materialStatus} onChange={e => setMaterialStatus(e.target.value)} className="w-full sm:w-40">
                            <option value="">All Material Status</option>
                            <option value="open">Open</option>
                            <option value="closed">Closed</option>
                        </FormSelect>
                        <div className="flex gap-2">
                            <Button type="submit" size="sm"><FunnelIcon className="w-4 h-4" /> Apply</Button>
                            <Button type="button" variant="outline" size="sm" onClick={clearFilters}>Clear</Button>
                        </div>
                    </form>
                </CardBody>
            </Card>

            <Card>
                <Table minWidth="900px">
                    <thead>
                        <tr>
                            <Th>SL</Th>
                            <Th>Req. No.</Th>
                            <Th>Supplier</Th>
                            <Th>Requested By</Th>
                            <Th>Status</Th>
                            <Th>Material</Th>
                            <Th>Total</Th>
                            <Th>Date</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {requisitions?.map?.((r, idx) => (
                            <Tr key={r.id}>
                                <Td className="font-semibold text-slate-600 text-xs">{requisitions.length - idx}</Td>
                                <Td className="font-medium text-slate-800 whitespace-nowrap">
                                    {r.formatted_number || String(r.number).padStart(4, '0')}
                                </Td>
                                <Td className="whitespace-nowrap">{r.supplier_name || '—'}</Td>
                                <Td className="whitespace-nowrap">{r.requested_by?.name || '—'}</Td>
                                <Td>{statusBadge(r.status)}</Td>
                                <Td>{materialStatusBadge(r.material_status)}</Td>
                                <Td className="font-medium whitespace-nowrap">
                                    {r.total_price != null ? `AED ${Number(r.total_price).toLocaleString()}` : '—'}
                                </Td>
                                <Td className="text-xs text-slate-500 whitespace-nowrap">
                                    {r.date
                                        ? r.date.split('T')[0].split('-').reverse().join('/')
                                        : r.created_at
                                            ? r.created_at.split('T')[0].split('-').reverse().join('/')
                                            : '—'}
                                </Td>
                                <Td>
                                    <div className="flex flex-wrap items-center gap-1">
                                        {can('materials.edit') && (r.status === 'pending' || isAdmin) && (
                                            <Link href={`/materials/${r.id}/edit`} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg transition">
                                                <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                                            </Link>
                                        )}
                                        {r.status === 'pending' && can('materials.approve') && (
                                            <>
                                                <button onClick={() => handleApprove(r.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition">
                                                    <CheckIcon className="w-3.5 h-3.5" /> Approve
                                                </button>
                                                <button onClick={() => setRejectModal(r.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-500 bg-red-50 hover:bg-red-100 rounded-lg transition">
                                                    <XMarkIcon className="w-3.5 h-3.5" /> Reject
                                                </button>
                                            </>
                                        )}
                                        <Link href={`/materials/${r.id}/invoices`} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition">
                                            <DocumentTextIcon className="w-3.5 h-3.5" /> Invoices
                                        </Link>
                                        {can('materials.reports') && (
                                            <>
                                                <a href={`/materials/${r.id}/report-pdf`} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition">
                                                    <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Export Requisition
                                                </a>
                                                <a href={`/materials/${r.id}/invoice-report-pdf`} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-slate-700 hover:bg-slate-800 rounded-lg transition">
                                                    <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Export Report
                                                </a>
                                            </>
                                        )}
                                        {can('materials.delete') && (
                                            <button onClick={() => setDeleteConfirm(r.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-500 bg-red-50 hover:bg-red-100 rounded-lg transition">
                                                <TrashIcon className="w-3.5 h-3.5" /> Delete
                                            </button>
                                        )}
                                    </div>
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
                {(!requisitions || requisitions.length === 0) && (
                    <EmptyState
                        icon={ClipboardDocumentListIcon}
                        title="No requisitions"
                        description="Create your first material requisition."
                        action={
                            <Button href="/materials/create" size="sm">
                                <PlusIcon className="w-4 h-4" /> New Requisition
                            </Button>
                        }
                    />
                )}
            </Card>

            {/* Delete Confirmation Modal */}
            <Modal
                show={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Delete Requisition"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<TrashIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">Are you sure you want to delete this requisition? This will also remove all associated invoices.</p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button variant="danger" onClick={() => { router.delete(`/materials/${deleteConfirm}`); setDeleteConfirm(null); }}>Delete</Button>
                </ModalFooter>
            </Modal>

            {/* Reject Modal */}
            <Modal
                show={!!rejectModal}
                onClose={() => { setRejectModal(null); setRejectedReason(''); }}
                title="Reject Requisition"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<XMarkIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600 mb-3">Are you sure you want to reject this requisition?</p>
                    <textarea
                        placeholder="Reason for rejection (optional)..."
                        value={rejectedReason}
                        onChange={e => setRejectedReason(e.target.value)}
                        rows={3}
                        className="w-full rounded-xl border border-slate-200 text-sm px-4 py-2.5 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20"
                    />
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => { setRejectModal(null); setRejectedReason(''); }}>Cancel</Button>
                    <Button variant="danger" onClick={handleReject}>Reject</Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
